import React, { useEffect, useRef, useState } from "react";
import {
  getGrbsRows,
  getVitalsRows,
} from "./CommonComponents/vitalsGrbs";
import {
  Collapse,
  Form,
  Button,
  message,
  Typography,
  Row,
  Col,
} from "antd";
import ConsultantForm from "./CommonComponents/ConsultantForm";
import PatientProfile from "./CommonComponents/PatientProfile";
import ClinicalImpression from "./CommonComponents/ClinicalImpression";
import HistoryIllness from "./CommonComponents/HistoryIllness";
import FunctionalStatus from "./CommonComponents/FunctionalStatus";
import ClinicalProgress from "./CommonComponents/ClinicalProgress/ClinicalProgress";
import Medication from "./CommonComponents/Medication";
import Vitals from "./CommonComponents/Vitals";
import DischargeFunctionalStatus from "./DischargeFunctionalStatus";
import Comments from "./CommonComponents/Comments";
import btnStyles from "../../../styles/Custom/Button.module.css";
import dayjs from "dayjs";
import "./ReviewForms.css";
import "./FormsIp.css";
import {
  getPatientDetailsApi,
  getListReferralFormApi,
  getReferralFormByIdApi,
  updateReferralFormByIdApi,
  updateReferralFormHtmlByIdApi,
  printFormApi,
  shareIpFormApi,
  submitReferralFormApi,
} from "../../../services/forms.services";
import FormCloseAction, {
  useLeavePatientForm,
} from "./CommonComponents/FormCloseAction";
import {
  knownAllergiesOptions,
  pastMedicalOptions,
} from "../../IpEMR/Options/historyOptions";
import {
  getLabelTagsWithFormDescription,
  getPastMedicalHistoryFormSummary,
  printFormSummary,
} from "./formsKeys";
import useQuery from "../../../hooks/useQuery";
import NoDataPage from "../../NoDataPages/NoDataPage";
import assessmentIcon from "../../../assets/images/nurseIcons/assessment.svg";
import FormContentLoader from "./FormContentLoader";
import FormDocumentView from "./CommonComponents/FormDocumentView";
import ShareModal from "../Forms/DischargeSummary/ShareModal";
import ReviewModal from "./ReviewForms/ReviewModal";
import {
  isHtmlDocument,
  isWithinEditWindow,
  normalizeRecordList,
} from "./CommonComponents/formRecords";


const ReferralForm = ({ patientData, role }) => {
  const leavePatientForm = useLeavePatientForm();
  // console.log(patientData);
  const [form] = Form.useForm();
  const formRef = useRef(null);
  const commonStyle = {
    // background: "#fff",
    boxShadow:
      "0px 2px 4px -2px rgba(16, 24, 40, 0.06), 0px 4px 8px -2px rgba(16, 24, 40, 0.1)",
  };

  const [pressureSorePoints, setPressureSorePoints] = useState([]);
  const [referralFormData, setReferralFormData] = useState(null);
  const [editable, setEditable] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [details, setDetails] = useState(null);
  const [displayForm, setDisplayForm] = useState(null);
  // Set on a successful submit so the saved document opens in the editor
  // instead of the read-only preview.
  const [openEditorAfterSubmit, setOpenEditorAfterSubmit] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [openShare, setOpenShare] = useState(false);
  const [sharePending, setSharePending] = useState(null);
  const [recordList, setRecordList] = useState([]);
  const [openHistory, setOpenHistory] = useState(false);
  const [isNewForm, setIsNewForm] = useState(false);

  const refreshPage = () => setRefresh((prev) => !prev);
  const isActive = useQuery()?.status === "current";
  const isReceptionist = role === "receptionist";
  const canEditForm = !isReceptionist && editable;

  const loadRecordDisplay = async (record, isCancelled = () => false) => {
    if (!record?.id) {
      if (!isCancelled()) setDisplayForm(null);
      return;
    }
    if (isHtmlDocument(record?.pdf_html)) {
      if (!isCancelled()) setDisplayForm(record.pdf_html);
      return;
    }
    try {
      const savedRes = await getReferralFormByIdApi(record.id);
      const saved = savedRes?.data?.data?.pdf_html ?? savedRes?.data?.pdf_html;
      if (isHtmlDocument(saved)) {
        if (!isCancelled()) setDisplayForm(saved);
        return;
      }
    } catch (err) {
      console.log(err);
    }
    try {
      const previewRes = await printFormApi(
        `form_type=referral&form_id=${record.id}`
      );
      if (!isCancelled()) setDisplayForm(previewRes?.data?.data ?? null);
    } catch (err) {
      console.log(err);
      if (!isCancelled()) setDisplayForm(null);
    }
  };

  useEffect(() => {
    if (!patientData?.admission?.id || !patientData?.doctor_intial_assessment) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    const isCancelled = () => cancelled;
    setIsLoading(true);
    setDisplayForm(null);

    const loadForm = async () => {
      try {
        const [listRes, detailsRes] = await Promise.all([
          getListReferralFormApi(patientData.admission.id).catch((err) => {
            if (err?.response?.status === 404) return null;
            throw err;
          }),
          getPatientDetailsApi(patientData.admission.id),
        ]);
        if (cancelled) return;

        const list = normalizeRecordList(listRes?.data);
        setRecordList(list);
        setDetails(detailsRes?.data);

        if (isNewForm) {
          setReferralFormData(null);
          setEditable(isActive && !isReceptionist);
          return;
        }

        // Whichever record is open stays selected across a refresh, so saving
        // an edit to an older entry keeps showing that entry rather than
        // jumping to the newest one.
        const openId = referralFormData?.data?.id;
        const record =
          list.find((row) => row?.id === openId) ??
          list[list.length - 1] ??
          null;

        setReferralFormData(record ? { data: record } : null);
        if (isReceptionist || !isActive) {
          setEditable(false);
        } else {
          setEditable(record?.id ? false : true);
        }

        await loadRecordDisplay(record, isCancelled);
      } catch (err) {
        console.log(err);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadForm();
    return () => {
      cancelled = true;
    };
  }, [patientData?.admission?.id, patientData?.doctor_intial_assessment, refresh, isReceptionist, isActive]);

  const handleNewForm = () => {
    setOpenEditorAfterSubmit(false);
    setIsNewForm(true);
    setReferralFormData(null);
    form.resetFields();
    setPressureSorePoints([]);
    setDisplayForm(null);
    setEditable(true);
    /* Care items, vitals and medication on a new form come from the
       patient details loaded with the page. A doctor who ordered
       something in another tab would otherwise fill this form against
       what was on screen when it opened, so it is re-read here. */
    refreshPage();
  };

  const handleHistoryAction = (record, isEdit) => {
    setOpenEditorAfterSubmit(false);
    form.resetFields();
    setIsNewForm(false);
    setOpenHistory(false);
    setReferralFormData(record ? { data: record } : null);
    setEditable(isEdit && !isReceptionist && isActive);
    setDisplayForm(null);
    setIsLoading(true);
    loadRecordDisplay(record).finally(() => setIsLoading(false));
  };

  const setInitialFormValues = () => {
    form.setFieldsValue({
      ...referralFormData?.data?.data,
      clinical_progress: {
        ...referralFormData?.data?.data?.clinical_progress,
        date: referralFormData?.data?.data?.clinical_progress?.date
          ? dayjs(referralFormData?.data?.data?.clinical_progress?.date) // Keep as dayjs object
          : null,
      },
    });
    if (referralFormData?.data?.data?.pressure_sore) {
      setPressureSorePoints(referralFormData?.data?.data?.pressure_sore);
    }
  };

  useEffect(() => {
    setInitialFormValues();
  }, [referralFormData?.data?.data]);

  /*
   * Adding a vitals reading used to call refreshPage, which re-ran the whole
   * load and reset the form - everything typed and not yet submitted went
   * with it. Only the patient details carry vitals, so only those are
   * re-read; the open form is left untouched.
   */
  const refreshVitals = () => {
    const admissionId = patientData?.admission?.id;
    if (!admissionId) return;
    getPatientDetailsApi(admissionId)
      .then((res) => setDetails(res?.data))
      .catch((err) => console.log(err));
  };

  const onFinish = (values) => {
    const formData = {
      patient: details?.data?.patient,
      diagnosis: details?.data?.diagnosis,
      medications: details?.data?.medications,
      functional_status: details?.data?.functional_status,
      vitals: getVitalsRows(details?.data),
      grbs: getGrbsRows(details?.data),
      nebulization: details?.data?.nebulization,
      past_medical_history: details?.data?.present_illness
        ? getPastMedicalHistoryFormSummary(
            pastMedicalOptions,
            details?.data?.present_illness
          )
        : "",
      known_allergies: details?.data?.present_illness
        ? getLabelTagsWithFormDescription(
            knownAllergiesOptions,
            details?.data?.present_illness
          )
        : "",
      past_surgical_history: details?.data?.present_illness?.surgical_history
        ? details?.data?.present_illness?.surgical_history
        : null,
      past_surgical_history_details: details?.data?.present_illness
        ?.surgical_history_details
        ? details?.data?.present_illness?.surgical_history_details
        : null,
      ...values,

      clinical_progress: {
        ...values?.clinical_progress,
        date: values?.clinical_progress?.date
          ? dayjs(values.clinical_progress.date).format("YYYY-MM-DD")
          : null, // Handle cases where the date is not provided
      },
      pressure_sore: pressureSorePoints,
    };
    const formId = referralFormData?.data?.id;
    if (!formId && !isNewForm && recordList?.length) {
      message.error(
        "Could not tell which form is open. Please reopen it from History and try again."
      );
      return;
    }
    if (formId) {
      updateReferralFormByIdApi(formId, { data: formData })
        .then((res) => {
          message.success(res?.data?.message);
          setEditable(false);
          // Drop the previous document so the editor cannot open on stale
          // html while the saved version is being refetched.
          setDisplayForm(null);
          setIsLoading(true);
          setOpenEditorAfterSubmit(true);
          refreshPage();
        })
        .catch((err) => message.error(err?.response?.data?.message));
    } else {
      submitReferralFormApi({
        ip_admission: patientData?.admission?.id,
        data: formData,
      })
        .then((res) => {
          message.success(res?.data?.message);
          setIsNewForm(false);
          setEditable(false);
          setDisplayForm(null);
          setIsLoading(true);
          setOpenEditorAfterSubmit(true);
          refreshPage();
        })
        .catch((err) => message.error(err?.response?.data?.message));
    }

    // console.log(formData, "fo");
  };

  const saveDocumentHtml = async (html, { refresh = true } = {}) => {
    const formId = referralFormData?.data?.id;
    if (!formId) return false;
    try {
      const res = await updateReferralFormHtmlByIdApi(formId, html);
      message.success(res?.data?.message || "Referral form saved.");
      // Re-read from the server rather than holding the saved html in state,
      // so what is displayed is always what the backend actually stored.
      if (refresh) refreshPage();
      return true;
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Failed to save. Please try again."
      );
      return false;
    }
  };

  return (
    <>
      {!patientData?.doctor_intial_assessment ? (
        <Row
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "80vh",
          }}
        >
          <Col span={24} style={{ textAlign: "center" }}>
            <NoDataPage
              title={"Initial Assessment Not Yet Submitted "}
              subtitle={"Kindly, submit initial assessment first"}
              image={assessmentIcon}
            />
          </Col>
        </Row>
      ) : (
        <FormContentLoader loading={isLoading} tip="Loading referral form...">
        <div style={{ position: "relative" }}>
          {canEditForm && (
            <div className="forms-ip-header">
              <Typography.Title level={5} className="forms-ip-header-title">
                Referral Form
              </Typography.Title>
              {/* The way out is offered whether or not a form has been saved
                  before: with one, the saved form is put back; without one,
                  there is nothing behind the editor and the form is left. */}
              <div className="forms-ip-header-actions">
                {referralFormData?.data?.id && (
                  <Button
                    type="primary"
                    onClick={() =>
                      printFormSummary(
                        "referral",
                        referralFormData?.data?.id,
                        displayForm
                      )
                    }
                  >
                    Print
                  </Button>
                )}
                <FormCloseAction
                  isDirty={() => form.isFieldsTouched()}
                  onClose={() => {
                    if (referralFormData?.data?.id) {
                      setEditable(false);
                      form.resetFields();
                      refreshPage();
                      return;
                    }
                    leavePatientForm();
                  }}
                />
              </div>
            </div>
          )}
          {canEditForm ? (
            <Form
              form={form}
              ref={formRef}
              layout="vertical"
              onFinish={onFinish}
              className="forms-ip-shell"
              initialValues={{
                ...referralFormData?.data?.data,
                clinical_progress: {
                  ...referralFormData?.data?.data?.clinical_progress,
                  date: referralFormData?.data?.data?.clinical_progress?.date
                    ? dayjs(
                        referralFormData?.data?.data?.clinical_progress?.date
                      ) // Keep as dayjs object
                    : null,
                },
              }}
            >
              <Collapse
                expandIconPosition="end"
                className="custom-collapse forms-ip-collapse"
                defaultActiveKey={[
                  "1",
                  "2",
                  "3",
                  "4",
                  "5",
                  "6",
                  "7",
                  "8",
                  "9",
                  "10",
                  "11",
                ]}
              >
                <Collapse.Panel
                  header="Patient Profile"
                  style={commonStyle}
                  key="1"
                >
                  <PatientProfile data={patientData?.admission} />
                </Collapse.Panel>
                <Collapse.Panel
                  header="Consultants"
                  style={commonStyle}
                  key="2"
                >
                  <ConsultantForm form={form} />
                </Collapse.Panel>
                <Collapse.Panel
                  header="Clinical Impression/ Diagnosis"
                  style={commonStyle}
                  key="3"
                >
                  <ClinicalImpression
                    form={form}
                    details={details?.data?.diagnosis}
                  />
                </Collapse.Panel>
                <Collapse.Panel
                  header="History of Present Illness"
                  style={commonStyle}
                  key="4"
                >
                  <HistoryIllness
                    form={form}
                    patientData={patientData?.admission}
                    data={details?.data?.present_illness}
                    details={details?.data}
                  />
                </Collapse.Panel>
                <Collapse.Panel
                  header="Functional Status at the time of Admission"
                  style={commonStyle}
                  key="5"
                >
                  <FunctionalStatus
                    form={form}
                    details={details?.data?.functional_status}
                  />
                </Collapse.Panel>
                <Collapse.Panel
                  header="Clinical Progress"
                  style={commonStyle}
                  key="6"
                >
                  <ClinicalProgress
                    form={form}
                    setPressureSorePoints={setPressureSorePoints}
                    pressureSorePoints={pressureSorePoints}
                  />
                </Collapse.Panel>
                <Collapse.Panel header="Medication" style={commonStyle} key="7">
                  <Medication form={form} details={details} />
                </Collapse.Panel>
                <Collapse.Panel
                  header="Functional Status at Discharge / Referral"
                  style={commonStyle}
                  key="9"
                >
                  <DischargeFunctionalStatus form={form} />
                </Collapse.Panel>
                <Collapse.Panel header="Vitals" style={commonStyle} key="10">
                  <Vitals
                    form={form}
                    details={details}
                    admissionId={patientData?.admission?.id}
                    onVitalsAdded={refreshVitals}
                  />
                </Collapse.Panel>
                <Collapse.Panel
                  header="Comments"
                  style={{ ...commonStyle, marginBottom: "40px" }}
                  key="11"
                >
                  <Comments form={form} />
                </Collapse.Panel>
              </Collapse>
              <div
                className="ip-fixed-form-footer"
                style={{
                  position: "fixed",
                  bottom: 0,
                  left: 0,

                  width: "100%",
                  background: "#fff",
                  paddingTop: "10px",
                  paddingRight: "16px",
                  boxShadow: "0px -2px 4px rgba(0, 0, 0, 0.1)",
                  textAlign: "right",
                  zIndex: 1000, // Ensure it stays above other elements
                }}
              >
                {isActive && !["receptionist"]?.includes(role) && (
                  <Form.Item>
                    <Button
                      htmlType="submit"
                      type="primary"
                      className={btnStyles.lgBtn}
                    >
                      {referralFormData?.data?.id ? "Update" : "Submit"}
                    </Button>
                  </Form.Item>
                )}
              </div>
            </Form>
          ) : isReceptionist && !referralFormData?.data?.id ? (
            <NoDataPage
              title="Referral Form Not Available"
              subtitle="Referral form has not been submitted yet"
              image={assessmentIcon}
            />
          ) : displayForm ? (
            <FormDocumentView
              html={displayForm}
              canEdit={
                isActive &&
                !isReceptionist &&
                isWithinEditWindow(referralFormData?.data?.created_at)
              }
              autoEdit={openEditorAfterSubmit}
              onAutoEditStart={() => setOpenEditorAfterSubmit(false)}
              onSave={saveDocumentHtml}
              onShare={(pendingHtml, onSaved) => {
                setSharePending(pendingHtml ? { pendingHtml, onSaved } : null);
                setOpenShare(true);
              }}
              leading={
                <div className="review-form-doc-leading">
                  <span className="review-form-title-text">Referral Form</span>
                  {!editable && isActive && !isReceptionist && (
                    <Button
                      className="ds-btn ds-btn--ghost"
                      onClick={handleNewForm}
                    >
                      New Form
                    </Button>
                  )}
                </div>
              }
              extraActions={
                <>
                  {recordList?.length > 0 && (
                    <Button
                      className="ds-btn ds-btn--ghost"
                      onClick={() => setOpenHistory(true)}
                    >
                      History
                    </Button>
                  )}
                  <Button
                    className="ds-btn ds-btn--ghost"
                    onClick={() =>
                      printFormSummary(
                        "referral",
                        referralFormData?.data?.id,
                        displayForm
                      )
                    }
                  >
                    Print
                  </Button>
                </>
              }
            />
          ) : (
            <NoDataPage
              title="Referral Form Not Available"
              subtitle="Referral form has not been submitted yet"
              image={assessmentIcon}
            />
          )}
          {openHistory && (
            <ReviewModal
              open={openHistory}
              setOpen={setOpenHistory}
              reviewList={recordList}
              isActive={isActive && !isReceptionist}
              title="Referral Form History"
              onShowClick={(record) => handleHistoryAction(record, false)}
            />
          )}
          {openShare && (
            <ShareModal
              openModal={openShare}
              setOpenModal={(open) => {
                setOpenShare(open);
                if (!open) {
              /* The save was held back from refreshing so the share could
                 address the record it had just written; refresh now that the
                 modal is done with it. */
              if (sharePending) refreshPage();
              setSharePending(null);
            }
              }}
              id={referralFormData?.data?.id}
              onBeforeSend={
                sharePending
                  ? async () => {
                      const ok = await saveDocumentHtml(sharePending.pendingHtml, {
                        refresh: false,
                      });
                      if (ok) sharePending.onSaved?.();
                      return ok;
                    }
                  : undefined
              }
              patientData={patientData}
              title="Share Referral Form"
              documentLabel="Referral form"
              shareApi={(payload) =>
                shareIpFormApi("referral", referralFormData?.data?.id, payload)
              }
            />
          )}
        </div>
        </FormContentLoader>
      )}
    </>
  );
};

export default ReferralForm;
